import React, {ChangeEvent, useEffect, useRef, useState} from 'react';
import Auth from "../../../models/response/auth.model";
import {useAppDispatch} from "../../../store";
import {useNavigate} from "react-router-dom";
import {fetchSearchResults} from "../../../store/actions";
import {useSelector} from "react-redux";

interface SearchBannerProps {
    auth: Auth;
    onSearchSubmit: (query: string) => void; // For Enter key submission
    onDynamicResults: (hasResults: boolean) => void; // For dropdown results
}

const SearchBanner = ({auth, onSearchSubmit, onDynamicResults}: SearchBannerProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [searchText, setSearchText] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [limit, setLimit] = useState(20);
    const [offset, setOffset] = useState(0);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const {searchResults} = useSelector<any, any>(({appWizard}) => appWizard);

    // Handle search API call
    const handleSearch = (query: string) => {
        dispatch<any>(fetchSearchResults(
            searchText !== "" ? searchText.split(" ").toString() : '0',
            limit,
            offset,
            []
        ));
    };

    // Handle input changes (typing)
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchText(query);

        if (query.trim() === "") {
            onDynamicResults(false);
            setShowDropdown(false);
            onSearchSubmit("");
            return;
        }

        // Trigger search for dropdown
        handleSearch(query);
    };

    // Handle Enter key press
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && searchText.trim() !== "") {
            onSearchSubmit(searchText); // Notify parent component
            setShowDropdown(false); // Hide dropdown when showing full results
        }
    };

    // Handle dropdown item selection
    const handleDropDownItem = (item: any) => {
        setShowDropdown(false);
        const route = auth && auth.accessToken !== ''
            ? `/discover/widget/${item.uuid}`
            : `/widget/${item.uuid}`;
        navigate(route);
    };

    // Handle clicks outside dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Update dropdown visibility when search results change
    useEffect(() => {
        if (searchResults && Array.isArray(searchResults)) {
            const hasResults = searchResults.length > 0;
            onDynamicResults(hasResults);
            setShowDropdown(hasResults && searchText.trim() !== "");
        }
    }, [searchResults]);

    return (
        <div style={{padding: "12px 0 64px 2vw"}}>
            <p style={{margin: "0 0 6px 0", fontWeight: 600, fontSize: "14px", color: "#333"}}>
                Search for apps
            </p>
            <div className="primary-searchbar__wrapper" style={{width: "18.75rem"}}>  {/* ← constrain width */}
                <input
                    type="text"
                    className="form-control primary-searchbar lg"
                    placeholder="Search by app name/keyword"
                    value={searchText}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    style={{
                        border: "1px solid black",
                        color: "black",
                        backgroundColor: "white",
                    }}
                />

                {showDropdown && searchResults?.length > 0 && (
                    <div className="nav-brand" ref={dropdownRef}
                         style={{
                             position: "relative",
                             width: "18.75rem",
                             minWidth: "18.75rem",
                             maxHeight: "200px",
                             overflowY: "auto",
                             zIndex: 9999,                              // ← sits above everything
                             background: "white",                      // ← solid background
                             border: "1px solid #ccc",
                             borderRadius: "4px",
                             boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                         }}>
                        <ul className="pop-dropdown-menu dropdown-menu-end mob-bottom-slider show widgets-show"
                            style={{listStyleType: "none", margin: 0, padding: 0}}>
                            {searchResults.map((item: any) => (
                                <li key={item.uuid}
                                    style={{
                                        background: "white",
                                        display: "block",
                                        borderRadius: "5px",
                                        cursor: "pointer"
                                    }}
                                    onMouseDown={(e) => {           // ← onMouseDown, not onClick
                                        e.preventDefault();         //   prevents blur stealing the click
                                        handleDropDownItem(item);
                                    }}>
                                    <div className="dropdown-item">
                                        <div
                                            className="profile-placeholder__wrapper left-align business-placeholder__wrapper">
                                            <div className="business-img-holder sm"
                                                 style={{backgroundImage: `url(${item.icon})`}}/>
                                            <div className="profile-details">
                                                <span className="main-title mt-bg">{item.title}</span>
                                                <span className="sub-title">{item.Category.title}</span>
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchBanner;
